import React, { useState, useRef } from 'react';
import {
  IconButton,
  Tooltip,
  Fade,
  Paper,
  styled,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Assignment as TaskIcon,
  Notifications as ReminderIcon,
  AlternateEmail as MentionIcon,
  FlashOn as QuickReplyIcon,
} from '@mui/icons-material';
import QuickTaskDialog from './QuickTaskDialog';
import QuickReminderDialog from './QuickReminderDialog';

interface QuickActionBarProps {
  onSendMessage: (content: string, agentId?: string) => void;
  onInsertText: (text: string) => void;
  agents: any[];
  conversationId?: string;
  disabled?: boolean;
  className?: string;
}

const ActionBar = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.5),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows[2],
  gap: theme.spacing(0.5),
  position: 'relative',
}));

const QuickButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.75),
  borderRadius: theme.spacing(1),
  transition: theme.transitions.create(['background-color', 'transform'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    transform: 'scale(1.05)',
    backgroundColor: theme.palette.action.hover,
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

const TaskButton = styled(QuickButton)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? '#E94257' : '#C73650',
  '&:hover': {
    backgroundColor: 'rgba(233, 66, 87, 0.1)',
  },
}));

const ReminderButton = styled(QuickButton)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? '#4DC4D2' : '#3A9BA5',
  '&:hover': {
    backgroundColor: 'rgba(77, 196, 210, 0.1)',
  },
}));

const MentionButton = styled(QuickButton)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? '#325BA8' : '#2A4D8F',
  '&:hover': {
    backgroundColor: 'rgba(50, 91, 168, 0.1)',
  },
}));

const QuickActionBar: React.FC<QuickActionBarProps> = ({
  onSendMessage,
  onInsertText,
  agents,
  conversationId,
  disabled = false,
  className,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showActions, setShowActions] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const actionBarRef = useRef<HTMLDivElement>(null);

  const handleToggleActions = () => {
    setShowActions(!showActions);
  };

  const handleCreateTask = () => {
    setTaskDialogOpen(true);
    setShowActions(false);
  };

  const handleCreateReminder = () => {
    setReminderDialogOpen(true);
    setShowActions(false);
  };

  const handleMentionAll = () => {
    onInsertText('@all ');
    setShowActions(false);
  };

  const handleQuickActions = [
    {
      id: 'task',
      label: 'Create Task',
      icon: <TaskIcon fontSize="small" />,
      color: 'task',
      action: handleCreateTask,
      description: 'Create a new task with Casey',
    },
    {
      id: 'reminder',
      label: 'Set Reminder',
      icon: <ReminderIcon fontSize="small" />,
      color: 'reminder',
      action: handleCreateReminder,
      description: 'Set a reminder or notification',
    },
    {
      id: 'mention',
      label: 'Mention All',
      icon: <MentionIcon fontSize="small" />,
      color: 'mention',
      action: handleMentionAll,
      description: 'Mention all teammates',
    },
  ];

  const getQuickButton = (action: typeof handleQuickActions[0]) => {
    const ButtonComponent = action.color === 'task' ? TaskButton :
                           action.color === 'reminder' ? ReminderButton :
                           action.color === 'mention' ? MentionButton :
                           QuickButton;

    return (
      <Tooltip
        key={action.id}
        title={action.description}
        placement="top"
        arrow
      >
        <ButtonComponent
          onClick={action.action}
          disabled={disabled}
          size={isMobile ? "medium" : "small"}
        >
          {action.icon}
        </ButtonComponent>
      </Tooltip>
    );
  };

  // Mobile version - always show all buttons
  if (isMobile) {
    return (
      <div className={className}>
        <ActionBar ref={actionBarRef} elevation={0}>
          {handleQuickActions.map(action => getQuickButton(action))}
        </ActionBar>

        <QuickTaskDialog
          open={taskDialogOpen}
          onClose={() => setTaskDialogOpen(false)}
          onSendMessage={onSendMessage}
          agents={agents}
          conversationId={conversationId}
        />

        <QuickReminderDialog
          open={reminderDialogOpen}
          onClose={() => setReminderDialogOpen(false)}
          onSendMessage={onSendMessage}
          conversationId={conversationId}
        />
      </div>
    );
  }

  // Desktop version - toggle behavior
  return (
    <div className={className}>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        {/* Main toggle button */}
        <Tooltip title="Quick Actions" placement="top">
          <QuickButton
            onClick={handleToggleActions}
            disabled={disabled}
            style={{
              backgroundColor: showActions ? theme.palette.action.selected : 'transparent',
              border: showActions ? `1px solid ${theme.palette.primary.main}` : 'none',
            }}
          >
            <QuickReplyIcon
              fontSize="small"
              style={{
                transform: showActions ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform'),
              }}
            />
          </QuickButton>
        </Tooltip>

        {/* Expanded action bar */}
        <Fade in={showActions}>
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginBottom: 8,
              zIndex: 1000,
            }}
          >
            <ActionBar ref={actionBarRef} elevation={4}>
              {handleQuickActions.map(action => getQuickButton(action))}
            </ActionBar>
          </div>
        </Fade>
      </div>

      <QuickTaskDialog
        open={taskDialogOpen}
        onClose={() => setTaskDialogOpen(false)}
        onSendMessage={onSendMessage}
        agents={agents}
        conversationId={conversationId}
      />

      <QuickReminderDialog
        open={reminderDialogOpen}
        onClose={() => setReminderDialogOpen(false)}
        onSendMessage={onSendMessage}
        conversationId={conversationId}
      />
    </div>
  );
};

export default QuickActionBar;